import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogTitle,
  IconButton,
  Typography,
} from "@suraasa/placebo-ui"
import { AssignmentAttachment } from "api/resources/assignments/types"
import clsx from "clsx"
import { Download, Xmark } from "iconoir-react"
import { useEffect, useRef, useState } from "react"
import { downloadFile, pluralize } from "utils/helpers"

import { getExtension } from "./AttachmentCard"
import AttachmentStack from "./AttachmentStack"

type Props = {
  open: boolean
  onClose: () => void
  attachments: AssignmentAttachment[]
}

const AttachmentsDialog = ({ open, onClose, attachments }: Props) => {
  const listElRef = useRef<HTMLDivElement | null>(null)
  const [showTopFade, setShowTopFade] = useState(false)
  const [showBottomFade, setShowBottomFade] = useState(false)

  const updateFades = () => {
    const el = listElRef.current
    if (!el) return

    setShowTopFade(el.scrollTop > 0)
    setShowBottomFade(el.scrollTop + el.clientHeight < el.scrollHeight - 1)
  }

  // Use a ref callback instead of useEffect so this runs as soon as the
  // list element actually exists, not one render late.
  const setListRef = (el: HTMLDivElement | null) => {
    listElRef.current = el
    if (el) updateFades()
  }

  useEffect(() => {
    if (!open) return

    window.addEventListener("resize", updateFades)
    return () => window.removeEventListener("resize", updateFades)
  }, [open])

  const handleDownloadAll = () => {
    attachments.forEach((attachment, index) => {
      // Stagger downloads so browsers don't block the extra ones. On mobile,
      // later iterations can still get popup-blocked once the click's user
      // activation is used up — skipBlockedFallback keeps that from
      // navigating the page away from the in-progress submission.
      setTimeout(
        () =>
          downloadFile(attachment.file, attachment.name, {
            skipBlockedFallback: true,
          }),
        index * 300
      )
    })
    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="flex !max-h-[510px] w-[calc(100%-16px)] !max-w-[430px] flex-col rounded-2xl p-2 sm:w-full sm:p-3.5">
        <div className="flex items-center justify-between">
          <AttachmentStack
            count={attachments.length}
            showCount={false}
            width={46}
            height={59}
            radius={5.33}
          />
          <DialogClose>
            <IconButton color="black" aria-label="Close">
              <Xmark />
            </IconButton>
          </DialogClose>
        </div>

        <DialogTitle className="mt-2 pb-2.5">
          <Typography variant="title2" className="text-onSurface-900">
            {pluralize("resource", attachments.length)}{" "}
            {attachments.length === 1 ? "is" : "are"} attached for this task
          </Typography>
        </DialogTitle>

        <div className="relative">
          <div
            className={clsx(
              "pointer-events-none absolute inset-x-0 -top-0.5 z-10 h-6 bg-gradient-to-b from-surface to-transparent transition-opacity duration-200",
              showTopFade ? "opacity-100" : "opacity-0"
            )}
          />
          <div
            ref={setListRef}
            onScroll={updateFades}
            className="flex max-h-[200px] flex-col gap-1.5 overflow-y-auto"
          >
            {attachments.map(attachment => (
              <div
                key={attachment.file}
                className="flex flex-row items-center justify-between gap-1.5 rounded-xl bg-onSurface-50 p-1.5"
              >
                <div className="flex min-w-0 flex-col gap-0.75">
                  <Typography
                    variant="strong"
                    className="break-words text-onSurface-900"
                  >
                    {attachment.name}
                  </Typography>
                  <Typography
                    variant="smallBody"
                    className="text-onSurface-500"
                  >
                    {getExtension(attachment.name).toUpperCase() || "FILE"}
                  </Typography>
                </div>
                <Button
                  onClick={() => downloadFile(attachment.file, attachment.name)}
                  endAdornment={
                    <Download className="!size-2 stroke-[1.5px] text-onSurface-900" />
                  }
                  className="shrink-0 border border-onSurface-300 bg-surface px-1.5 py-[7px] text-strongSmallBody text-onSurface-700"
                >
                  Download
                </Button>
              </div>
            ))}
          </div>
          <div
            className={clsx(
              "pointer-events-none absolute inset-x-0 -bottom-0.5 z-10 h-6 bg-gradient-to-t from-surface to-transparent transition-opacity duration-200",
              showBottomFade ? "opacity-100" : "opacity-0"
            )}
          />
        </div>

        <DialogFooter className="m-0 flex justify-end border-0 px-0 pb-0 pt-2">
          <Button
            className="bg-onSurface-900 text-surface"
            onClick={handleDownloadAll}
          >
            Download all
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default AttachmentsDialog
